import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { VoucherResponse, VoucherCreate, VoucherUpdate } from '@/app/types';
import { listVoucherTypes } from '@/app/services/voucherService';
import { listParties } from '@/app/services/partyService';
import { listSites } from '@/app/services/siteService';

// Define the schema for the form
const formSchema = z.object({
  voucher_type_id: z.number({
    required_error: "Voucher type is required",
  }),
  voucher_number: z.string().optional(),
  reference_number: z.string().optional().nullable(),
  transaction_date: z.date({
    required_error: "Transaction date is required",
  }),
  due_date: z.date().optional().nullable(),
  narration: z.string().optional().nullable(),
  party_id: z.number().optional().nullable(),
  site_id: z.number().optional().nullable(),
  total_amount: z.number({
    required_error: "Total amount is required",
  }).min(0, "Amount must be 0 or positive"),
});

type FormValues = z.infer<typeof formSchema>;

interface VoucherFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: VoucherCreate | VoucherUpdate) => void;
  defaultValues?: VoucherResponse;
  isLoading: boolean;
  mode: 'create' | 'edit';
}

const VoucherForm: React.FC<VoucherFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading,
  mode,
}) => {
  // Fetch voucher types, parties, and sites for dropdowns
  const { data: voucherTypes = { items: [] } } = useQuery({
    queryKey: ['voucherTypes'],
    queryFn: () => listVoucherTypes({ limit: 100 }), // Get all types for dropdown
    enabled: open, // Only fetch when dialog is open
  });

  const { data: parties = { items: [] } } = useQuery({
    queryKey: ['parties'],
    queryFn: () => listParties({ limit: 100 }), // Get all parties for dropdown
    enabled: open,
  });

  const { data: sites = { items: [] } } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }), // Get all sites for dropdown
    enabled: open,
  });

  // Initialize the form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      voucher_type_id: defaultValues?.voucher_type_id || 0,
      voucher_number: defaultValues?.voucher_number || '',
      reference_number: defaultValues?.reference_number || '',
      transaction_date: defaultValues?.transaction_date ? new Date(defaultValues.transaction_date) : new Date(),
      due_date: defaultValues?.due_date ? new Date(defaultValues.due_date) : undefined,
      narration: defaultValues?.narration || '',
      party_id: defaultValues?.party_id || undefined,
      site_id: defaultValues?.site_id || undefined,
      total_amount: defaultValues?.total_amount || 0,
    },
  });

  // When the selected voucher type changes, update the form behavior
  useEffect(() => {
    const voucherTypeId = form.watch('voucher_type_id');
    const voucherType = voucherTypes.items.find(vt => vt.id === voucherTypeId);
    
    if (voucherType) {
      if (voucherType.numbering_method === 'auto' && mode === 'create') {
        // For auto numbering in create mode, reset voucher number field
        form.setValue('voucher_number', '');
      }
    }
  }, [form.watch('voucher_type_id'), form, voucherTypes.items, mode]);

  // When the dialog closes, reset the form
  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      form.reset();
    }
    onOpenChange(open);
  };

  // Handle form submission
  const handleSubmit = (values: FormValues): void => {
    // Format dates as YYYY-MM-DD strings for API
    const formattedValues = {
      ...values,
      transaction_date: format(values.transaction_date, 'yyyy-MM-dd'),
      due_date: values.due_date ? format(values.due_date, 'yyyy-MM-dd') : null,
    };
    onSubmit(formattedValues);
  };

  // Get the numbering method of the currently selected voucher type
  const selectedType = voucherTypes.items.find(
    vt => vt.id === form.watch('voucher_type_id')
  );
  const isManualNumbering = selectedType?.numbering_method === 'manual';
  const isVoucherNumberRequired = isManualNumbering && mode === 'create';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create' : 'Edit'} Voucher</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Voucher Type */}
              <FormField
                control={form.control}
                name="voucher_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Voucher Type</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(parseInt(value, 10))}
                      defaultValue={field.value ? field.value.toString() : undefined}
                      disabled={mode === 'edit'} // Cannot change type in edit mode
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select voucher type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {voucherTypes.items.map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Voucher Number (only required for manual numbering) */}
              <FormField
                control={form.control}
                name="voucher_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Voucher Number</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={isManualNumbering ? "Enter voucher number" : "Auto-generated"}
                        {...field}
                        value={field.value || ''}
                        disabled={!isManualNumbering || mode === 'edit'} // Only editable for manual numbering in create mode
                        required={isVoucherNumberRequired}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Date fields */}
            <div className="grid grid-cols-2 gap-4">
              {/* Transaction Date */}
              <FormField
                control={form.control}
                name="transaction_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Transaction Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date > new Date() || date < new Date("1900-01-01")
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Due Date (Optional) */}
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Due Date (Optional)</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date < new Date("1900-01-01")
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Reference Number */}
            <FormField
              control={form.control}
              name="reference_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference Number (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="External reference/invoice number"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Party */}
              <FormField
                control={form.control}
                name="party_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Party (Optional)</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value, 10))}
                      defaultValue={field.value ? field.value.toString() : "none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select party" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {parties.items.map((party) => (
                          <SelectItem key={party.id} value={party.id.toString()}>
                            {party.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Site */}
              <FormField
                control={form.control}
                name="site_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Site (Optional)</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value, 10))}
                      defaultValue={field.value ? field.value.toString() : "none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select site" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {sites.items.map((site) => (
                          <SelectItem key={site.id} value={site.id.toString()}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Total Amount */}
            <FormField
              control={form.control}
              name="total_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Amount</FormLabel>
                  <FormControl>
                    <Input 
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      value={field.value || 0}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Narration */}
            <FormField
              control={form.control}
              name="narration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Narration (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Additional notes or description"
                      className="resize-none"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === 'create' ? 'Creating...' : 'Updating...'}
                  </>
                ) : (
                  <>{mode === 'create' ? 'Create' : 'Update'}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default VoucherForm; 